import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAdminEntity from '../../../hooks/useAdminEntity';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getInvestorProjectDetailEndpoint } from '../services/investorService';
import {
  getCharacteristicsMasterPlanEndpoint,
  getMasterPlanProjectEndpoint,
  createMasterPlanProjectEndpoint,
  updateMasterPlanProjectEndpoint,
  uploadMasterPlanCoverEndpoint,
  uploadMasterPlanBlueprintsEndpoint,
} from '../services/masterPlanService';
import {
  ICharacteristic,
  MasterPlanCharacteristic,
  RetrieveMasterPlan,
} from '../models/masterPlanModel';
import { InvestorProjectDetail } from '../models/investors';

interface Props {
  projectId: string;
}

const useMasterPlan = ({ projectId }: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();

  const [sweetAlert, setSweetAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});
  // undefined = still loading, null = loaded but doesn't exist
  const [masterPlan, setMasterPlan] = useState<
    RetrieveMasterPlan | null | undefined
  >(undefined);
  const [masterPlanLoaded, setMasterPlanLoaded] = useState(false);

  // Characteristics rows being edited
  const [rows, setRows] = useState<ICharacteristic[]>([]);
  const [pendingKey, setPendingKey] = useState('');
  const [pendingValue, setPendingValue] = useState('');

  const showError = useCallback((text: string) => {
    setAlertProps({
      warning: true,
      title: 'Error',
      text,
      onConfirm: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  }, []);

  const showSuccess = useCallback(() => {
    setAlertProps({
      success: true,
      title: 'Guardado',
      text: 'Las características han sido guardadas correctamente.',
      onConfirm: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  }, []);

  const getProject = useCallback(
    () => getInvestorProjectDetailEndpoint(projectId, token),
    [projectId, token],
  );
  const [project] = useAdminEntity<InvestorProjectDetail>(
    getProject,
    showError,
  );

  const getCatalog = useCallback(
    () => getCharacteristicsMasterPlanEndpoint(token),
    [token],
  );
  const [catalog] = useAdminEntity<MasterPlanCharacteristic[]>(
    getCatalog,
    showError,
  );

  // Load master plan after catalog arrives (404 = not found, not an error)
  useEffect(() => {
    if (!catalog) return;
    const load = async () => {
      const { status, data } = await callEndpoint(
        getMasterPlanProjectEndpoint(projectId, token),
      );
      let found: RetrieveMasterPlan | null = null;
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        found = response.data as RetrieveMasterPlan;
      }
      setMasterPlan(found);
      setMasterPlanLoaded(true);
      setRows(found?.characteristics ?? []);
    };
    load();
  }, [catalog, projectId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const isNew = masterPlan === null;

  const availableCatalog: MasterPlanCharacteristic[] = (catalog || []).filter(
    (c) => !rows.some((r) => r.key === c.key),
  );

  const addRow = useCallback(() => {
    if (!pendingKey) return;
    setRows((prev) => [...prev, { key: pendingKey, value: pendingValue }]);
    setPendingKey('');
    setPendingValue('');
  }, [pendingKey, pendingValue]);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const updateValue = useCallback((key: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  }, []);

  const onSubmit = async () => {
    const payload = {
      characteristics: rows.map(({ key, value }) => ({ key, value })),
    };

    if (isNew) {
      const { status, data } = await callEndpoint(
        createMasterPlanProjectEndpoint(projectId, payload, token),
      );
      if (status === 200 || status === 201) {
        const response = fetchResponseAdapter(data);
        setMasterPlan(response.data as RetrieveMasterPlan);
        showSuccess();
      }
    } else if (masterPlan) {
      const { status } = await callEndpoint(
        updateMasterPlanProjectEndpoint(
          projectId,
          masterPlan.id,
          payload,
          token,
        ),
      );
      if (status === 200) {
        showSuccess();
      }
    }
  };

  const handleUploadCover = useCallback(
    async (files: Array<any>) => {
      if (!masterPlan || files.length === 0) return;
      const formData = new FormData();
      formData.append('cover', files[0]);
      const { status } = await callEndpoint(
        uploadMasterPlanCoverEndpoint(projectId, masterPlan.id, formData, token),
      );
      if (status === 200) navigate(0);
    },
    [masterPlan, projectId, callEndpoint, navigate, token],
  );

  const handleUploadBlueprints = useCallback(
    async (entries: Array<{ view: string; file: File }>) => {
      if (!masterPlan || entries.length === 0) return;
      const formData = new FormData();
      entries.forEach(({ view, file }) => formData.append(view, file));
      const { status } = await callEndpoint(
        uploadMasterPlanBlueprintsEndpoint(projectId, masterPlan.id, formData, token),
      );
      if (status === 200) navigate(0);
    },
    [masterPlan, projectId, callEndpoint, navigate, token],
  );

  const isLoading = !project || !catalog || !masterPlanLoaded;

  return {
    project,
    catalog,
    masterPlan,
    isNew,
    isLoading,
    rows,
    availableCatalog,
    pendingKey,
    setPendingKey,
    pendingValue,
    setPendingValue,
    addRow,
    removeRow,
    updateValue,
    onSubmit,
    handleUploadCover,
    handleUploadBlueprints,
    sweetAlert,
    alertProps,
  };
};

export default useMasterPlan;
